import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type { SourceType, SyncScope, TriggerSyncResponse } from '../types/source-system';

type State =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; response: TriggerSyncResponse }
  | { status: 'error'; message: string };

/**
 * Provides a `trigger` function that calls POST /project/:uuid/source/:resourceUuid/sync.
 * Callers should refresh useSourceSystem state after a successful trigger.
 */
export function useManualSync(projectUuid: string, _sourceType: SourceType, syncScope?: SyncScope) {
  const [state, setState] = React.useState<State>({ status: 'idle' });

  const trigger = React.useCallback(
    async (resourceUuid: string) => {
      setState({ status: 'submitting' });
      try {
        const response = await sourceSystemApi.triggerSync(projectUuid, resourceUuid, syncScope);
        setState({ status: 'success', response });
        // Auto-clear success banner after 4 s
        setTimeout(() => setState({ status: 'idle' }), 4_000);
        return response;
      } catch (err) {
        setState({ status: 'error', message: (err as Error).message });
        return null;
      }
    },
    [projectUuid, syncScope]
  );

  const reset = React.useCallback(() => setState({ status: 'idle' }), []);

  return { state, trigger, reset };
}
